import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import FinalCTA from "@/components/FinalCTA";
import { CORE_VALUES, IMAGES } from "@/lib/content";
import { ShieldCheck, Compass, Zap } from "lucide-react";

const ICONS = { ShieldCheck, Compass, Zap };

const About = () => {
  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="About Us"
          title="About Abayle Logistics and Trading"
          subtitle="A Somali company delivering trusted support across logistics, trade, clearance, car sales, and car rental."
          image={IMAGES.aboutPort}
        />

        {/* Who We Are */}
        <section className="bg-white" data-testid="about-who">
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-5 gap-8 sm:gap-14 items-start">
            <div className="lg:col-span-2">
              <SectionTitle eyebrow="Who We Are" title="A Somali Partner You Can Rely On" />
            </div>
            <div className="lg:col-span-3 text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
              <p>
                Abayle Logistics and Trading is a Somali company established to provide dependable service across
                movement, trade, operational mobility, and client support requirements. The company is presented as a
                trusted partner for cargo coordination, trade support, customs related assistance, vehicle solutions,
                and broader commercial service needs in Somalia.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="bg-abayle-green-dark text-white relative isolate overflow-hidden" data-testid="about-mission">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #C9A13B 0, transparent 40%)" }} />
          <div className="container-abayle py-14 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center relative">
            <div>
              <SectionTitle eyebrow="Our Mission" title="Moving Cargo. Enabling Trade. Supporting Clients." invert />
            </div>
            <p className="text-white/85 text-base lg:text-lg leading-relaxed">
              To make cargo move efficiently, trade flow reliably, and clients feel supported at every step through
              responsive service, professional discipline, and practical local expertise.
            </p>
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-abayle-ivory" data-testid="about-values">
          <div className="container-abayle py-14 sm:py-20 lg:py-28">
            <SectionTitle centered eyebrow="Our Core Values" title="Principles that Shape Our Work" />
            <div className="grid md:grid-cols-3 gap-6">
              {CORE_VALUES.map((v, idx) => {
                const Icon = ICONS[v.icon];
                return (
                  <div key={v.title} className="card-abayle text-center" data-testid={`value-${idx}`}>
                    <div className="mx-auto h-16 w-16 rounded-full bg-abayle-green flex items-center justify-center border-2 border-abayle-gold mb-5">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-abayle-green font-bold text-xl mb-3">{v.title}</h3>
                    <p className="text-abayle-charcoal/75 text-[15px] leading-relaxed">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Market Position */}
        <section className="bg-white" data-testid="about-position">
          <div className="container-abayle py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-8 sm:gap-14 items-center">
            <div>
              <SectionTitle eyebrow="Market Position" title="Built for the Somali Market" />
              <p className="text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
                Abayle is positioned to support clients operating in one of the region's most dynamic commercial
                environments. With Mogadishu as its base and an integrated service structure, the company is designed
                to support the movement of goods, coordination of trade processes, service delivery, and mobility
                needs in a way that builds client confidence.
              </p>
              <p className="mt-4 text-abayle-charcoal/80 leading-relaxed">
                Our positioning is anchored in Somalia's strategic trade geography and growing cargo flows.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-abayle-gold z-10" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-abayle-gold z-10" />
              <img src={IMAGES.logistics} alt="Logistics operations" className="w-full h-[280px] sm:h-[380px] lg:h-[420px] object-cover rounded-sm shadow-xl" />
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default About;
